import React, { useContext, useEffect, useMemo, useState } from "react";
import { Modal, ModalBody } from "@vds/modals";
import { Title } from "@vds/typography";
import { Toggle, Input } from "@vds/inputs";
import { css } from "@emotion/react";
import { FlexRow, Spacer, SLNotification, SLGridLayout, SLRowLayout, CompactCol } from "../../components/core/utility";
import { Icon } from "@vds/icons";
import { generate } from "shortid";
import { VIN_REGEX } from "../../constants/C4SValidationConstants";
import { getVehicleMake } from "../../services/C4S Decommission - Replacement/getVehicleMake";
import { getVehicleModel } from "../../services/C4S Decommission - Replacement/getVehicleModel";
import { validateVin } from "../../services/C4S Decommission - Replacement/validateVin";
import { CustomDropdown, CustomCheckbox, StatusWrapper } from "../C4S Decommission - Replacement/common/styledComponents";
import { C4SContext } from "../../context/c4sContext";
import { Loader } from "@vds/loaders";
import useOutsideClick from "../../customHook/use-outside-click";
import { changeVehicle } from "../../services/C4S Decommission - Replacement/editVehicleWTA";
import { Button } from "@vds/buttons";

export const StyledModal = css`
  max-height: 99vmin;
  padding: 1rem 1rem 1rem 1rem;
  padding-right: 2px;
  border-radius: 0.5rem !important;
  @media (max-width: 767px) {
    width: 560px !important;
    height: auto;
    min-height: 14.5rem;
    padding: 1rem 1rem 1rem 1rem;
    padding-right: 2px;
  }

  & footer {
    padding: 0;
  }
`;

const VEHICLE_FIELDS = {
  VIN: "Vin",
  YEAR: "Year",
  MAKE: "Make",
  MODEL: "Model",
  WHEELCHAIR: "Check1",
  KILLSWITCH: "Check2",
  AMBULANCE: "Check3",
};

const VehicleChangeModal = ({ showChangeVehicleModal, setShowChangeVehicleModal, handleConfirmChange, data, bundleName }) => {
  const { vehicle, year: currentYear, make: currentMake, model: currentModel } = data;
  const { cspDetails } = useContext(C4SContext);

  const [notification, setNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [wheelChair, setWheelChair] = useState(false);
  const [killSwitch, setKillSwitch] = useState(false);
  const [ambulance, setAmbulance] = useState(false);
  const [vinValue, setVinValue] = useState({
    value: "",
    error: false,
    errorText: "Enter valid vin",
    success: false,
  });
  const [isToggle, setIsToggle] = useState(false);
  const [yearList, setYearList] = useState([]);
  const [makeList, setMakeList] = useState([]);
  const [modelList, setModelList] = useState([]);
  const [year, setYear] = useState({
    value: "",
    error: false,
    errorText: "Please validate the Year.",
    success: false,
  });
  const [make, setMake] = useState({
    value: "",
    error: false,
    errorText: "Please validate the Make.",
    success: false,
  });

  const [model, setModel] = useState({
    value: "",
    error: false,
    errorText: "Please validate the Model.",
    success: false,
  });

  const [vinData, setVinData] = useState([]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setShowChangeVehicleModal(false);
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, []);

  useEffect(() => {
    getYearRange();
  }, []);

  const getVehicleMakeData = async (value) => {
    setIsLoading(true);
    let payload = { country: cspDetails?.cspDetails?.cspData?.data?.data?.BillingCountry, year: value };
    let response = await getVehicleMake(payload);
    if (response.status === 200 && response.data?.data?.length) {
      setMakeList(response?.data?.data);
      setIsLoading(false);
    } else {
      setIsLoading(false);
      notify("Something went wrong, please try again.", "error");
      setTimeout(() => {
        setNotification(null);
      }, 5000);
    }
  };

  const getVehicleModelData = async (value, year) => {
    setIsLoading(true);
    let payload = { country: cspDetails?.cspDetails?.cspData?.data?.data?.BillingCountry, year: year, make: value };
    let response = await getVehicleModel(payload);
    if (response.status === 200 && response.data?.data?.length) {
      setModelList(response?.data?.data);
      setIsLoading(false);
    } else {
      setIsLoading(false);
      notify("Something went wrong, please try again.", "error");
      setTimeout(() => {
        setNotification(null);
      }, 5000);
    }
  };

  const handleClickOutside = () => {
    setNotification(null);
  };
  const clickRef = useOutsideClick(handleClickOutside);

  const notify = (content, type) => {
    setNotification(<SLNotification key={generate()} type={type} title={content} fullBleed={true} ref={clickRef} />);
  };

  const getYearRange = () => {
    const startYear = 1981;
    const endYear = new Date().getFullYear() + 1;
    const years = [];

    for (let i = startYear; i <= endYear; i++) {
      years.push(i);
    }
    setYearList(years);
  };

  const handleFields = (field, value, year) => {
    switch (field) {
      case VEHICLE_FIELDS.VIN: {
        if (value && value?.length) {
          if (VIN_REGEX.test(value) && value.length === 17) {
            setVinValue({ value, error: false, errorText: "", success: true });
          } else {
            setVinValue({ value, error: true, errorText: "Enter a valid Vin", success: false });
            break;
          }
        } else {
          setVinValue({ value: "", error: true, errorText: "Enter Vin", success: false });
        }
        break;
      }

      case VEHICLE_FIELDS.YEAR: {
        getVehicleMakeData(value);

        value && value.length
          ? setYear({ value, error: false, errorText: "Select year", success: true })
          : setYear({ value: "", error: true, errorText: "Select year", success: false });
        break;
      }

      case VEHICLE_FIELDS.MAKE: {
        getVehicleModelData(value, year);

        value && value.length
          ? setMake({ value, error: false, errorText: "Select make", success: true })
          : setMake({ value: "", error: true, errorText: "Select make", success: false });
        break;
      }

      case VEHICLE_FIELDS.MODEL: {
        value && value.length
          ? setModel({ value, error: false, errorText: "Select model", success: true })
          : setModel({ value: "", error: true, errorText: "Select model", success: false });
        break;
      }

      case VEHICLE_FIELDS.WHEELCHAIR: {
        setWheelChair(!wheelChair);
        break;
      }

      case VEHICLE_FIELDS.KILLSWITCH: {
        setKillSwitch(!killSwitch);
        break;
      }

      case VEHICLE_FIELDS.AMBULANCE: {
        setAmbulance(!ambulance);
        break;
      }
    }
  };

  const disableConfirmChange = useMemo(() => {
    if (!isToggle) {
      return vinValue.success ? false : true;
    } else {
      return year.success && make.success && model.success ? false : true;
    }
  }, [isToggle, vinValue, year, make, model]);

  const getValidateVin = async (vinArray) => {
    setIsLoading(true);
    let payload = {
      billingCountry: cspDetails?.cspDetails?.cspData?.data?.data?.BillingCountry,
      bundleName: bundleName,
      vinList: vinArray,
    };
    let response = await validateVin(payload);
    let viValidationResponse = null;
    if (response.status === 200 && response.data?.data) {
      if (response.data.data.incompatibleVins?.length) {
        setVinValue((prevState) => ({
          ...prevState,
          error: true,
          errorText: "Incompatible VIN with the selected bundle.",
          success: false,
        }));
      } else if (response.data.data.invalidVins?.length) {
        setVinValue((prevState) => ({
          ...prevState,
          error: true,
          errorText: "Invalid VIN",
          success: false,
        }));
      } else if (response.data.data.validVins?.length) {
        setVinValue((prevState) => ({
          ...prevState,
          error: false,
        }));

        const builtVinData = response.data.data.validVins.map((item) => ({
          vin: item.vin,
          year: item.year,
          make: item.make,
          model: item.model,
          weightClass: item.weightClass,
          fuelType: item.fuelType,
          killSwitch: killSwitch,
          emergency: ambulance,
          wheelChair: wheelChair,
          quantity: 1,
        }));
        viValidationResponse = builtVinData;
        setVinData(builtVinData);
      }

      setIsLoading(false);
    } else {
      setIsLoading(false);
      notify("Something went wrong, please try again.", "error");
      setTimeout(() => {
        setNotification(null);
      }, 5000);
    }
    return viValidationResponse;
  };

  const onConfirmChangeClick = async () => {
    try {
      // validate VIN
      let payload = {};
      if (vinValue.value && vinValue.value.length && isToggle === false) {
        const viValidationResponse = await getValidateVin([vinValue.value]);
        if (viValidationResponse && viValidationResponse?.length) {
          payload = {
            id: data.id,
            vin: vinValue.value,
            year: viValidationResponse[0]?.year,
            make: viValidationResponse[0]?.make,
            model: viValidationResponse[0]?.model,
            killSwitch: killSwitch,
            emergency: ambulance,
            wheelChair: wheelChair,
            weightClass: viValidationResponse[0].weightClass,
            fuelType: viValidationResponse[0].fuelType,
          };
        }
      } else {
        // YMM case
        payload = {
          id: data.id,
          year: year.value,
          make: make.value,
          model: model.value,
          killSwitch: killSwitch,
          emergency: ambulance,
          wheelChair: wheelChair,
        };
      }

      if (!payload || Object.keys(payload).length === 0) {
        notify("Please provide valid vehicle information.", "error");
        return;
      }
      const response = await changeVehicle(payload);
      if (response.status === 200) {
        setShowChangeVehicleModal(false);
        handleConfirmChange(); // reload vehicle
      } else if (response?.status === 409) {
        const message = vinValue.value?.length
          ? `Following VIN already exist: [${vinValue.value}]`
          : response?.data?.message || "Duplicate vehicle found.";
        notify(message, "error");
        setTimeout(() => setNotification(null), 5000);
      } else {
        notify("Failed to change vehicle. Please try again.", "error");
        setTimeout(() => {
          setNotification(null);
        }, 5000);
      }
    } catch (err) {
      notify("Unexpected error. Please try again.", "error");
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  return (
    <>
      <Modal
        role="dialog"
        ariaLabel="Confirmation Modal"
        hideCloseButton={false}
        opened={showChangeVehicleModal}
        disableOutsideClick
        css={StyledModal}
        closeButton={
          <FlexRow justifyContent="space-between" onClick={(event) => event.preventDefault()}>
            <div>
              <Title size="medium">Change Vehicle</Title>
            </div>
            <div
              data-testId="close-icon"
              onClick={() => {
                setShowChangeVehicleModal(false);
              }}
              style={{ marginRight: "1rem", marginTop: "0.3rem" }}
            >
              <Icon
                aria-label="close"
                alt="close-icon"
                role="img"
                name="close"
                size="medium"
                color="#000000"
                style={{ cursor: "pointer", outline: "none" }}
              />
            </div>
          </FlexRow>
        }
      >
        <ModalBody css={{ paddingLeft: "0", paddingRight: "0", marginTop: "0.5rem" }}>
          {notification}
          <Loader active={isLoading} />
          <div style={{ fontFamily: "Verizon-NHG-eDS", fontSize: "14px" }}>
            Current: {vehicle.startsWith("VIN") ? "" : `${vehicle} -`} {currentYear} {currentMake} {currentModel}
          </div>
          <Spacer top="1.5rem" />
          {/* VIN/YMM */}
          <FlexRow>
            <p style={{ fontFamily: "Verizon-NHG-eTX", fontSize: "12px" }}>VIN</p>
            <Toggle
              value="default"
              surface="light"
              data-testid="toggle-btn"
              disabled={false}
              showText={false}
              textWeight="regular"
              textSize="small"
              textPosition="left"
              css={{ marginTop: "8px", marginLeft: "5px", marginRight: "5px" }}
              onChange={() => {
                // when its YMM reset vin field
                if (!isToggle) {
                  setVinValue({ value: "", error: false, errorText: "Enter valid vin", success: false });
                }
                setIsToggle(!isToggle);
              }}
            />
            <p style={{ fontFamily: "Verizon-NHG-eTX", fontSize: "12px" }}>YMM</p>
          </FlexRow>
          {!isToggle ? (
            <FlexRow css={{ padding: "5px !important" }}>
              <Input
                value={vinValue.value}
                label="VINs"
                data-testid="vin-input"
                error={vinValue.error}
                errorText={vinValue.errorText}
                success={vinValue.success}
                width={"95%"}
                css={{ fontSize: "12px !important" }}
                onChange={(event) => handleFields(VEHICLE_FIELDS.VIN, event.target.value, "")}
              />
              {vinValue.success && vinData.length ? (
                <div style={{ fontSize: "14px", paddingTop: "14px" }}>
                  {vinValue.value} - {vinData[0].year} {vinData[0].make} {vinData[0].model}
                </div>
              ) : (
                <></>
              )}
            </FlexRow>
          ) : (
            <SLGridLayout>
              <SLRowLayout css={{ marginTop: ".25rem", paddingLeft: "10px !important", maxWidth: "95%" }}>
                <CompactCol colSizes={{ mobile: 2, tablet: 6, desktop: 13 }}>
                  <SLGridLayout>
                    <SLRowLayout css={{ marginTop: "1.25rem", paddingLeft: "10px !important" }}>
                      <CompactCol colSizes={{ mobile: 4, tablet: 12, desktop: 4 }}>
                        <CustomDropdown
                          label="Year"
                          value={year.value ? year.value : "Please select"}
                          errorText={year.errorText}
                          error={year.error}
                          success={year.success}
                          onChange={(e) => handleFields(VEHICLE_FIELDS.YEAR, e.target.value, "")}
                          marginBottom="10px"
                          paddingRight="5px"
                        >
                          <option disabled>Please select</option>
                          {yearList.map((record) => (
                            <option key={record} value={record}>
                              {record}
                            </option>
                          ))}
                        </CustomDropdown>
                      </CompactCol>
                      <CompactCol colSizes={{ mobile: 4, tablet: 12, desktop: 4 }}>
                        <CustomDropdown
                          label="Make"
                          value={make.value ? make.value : "Please select"}
                          errorText={make.errorText}
                          error={make.error}
                          success={make.success}
                          onChange={(e) => handleFields(VEHICLE_FIELDS.MAKE, e.target.value, year.value)}
                          marginBottom="10px"
                          paddingRight="5px"
                        >
                          <option disabled>Please select</option>
                          {makeList.map((record) => (
                            <option key={record} value={record}>
                              {record}
                            </option>
                          ))}
                        </CustomDropdown>
                      </CompactCol>
                      <CompactCol colSizes={{ mobile: 4, tablet: 12, desktop: 4 }}>
                        <CustomDropdown
                          label="Model"
                          value={model.value ? model.value : "Please select"}
                          errorText={model.errorText}
                          error={model.error}
                          success={model.success}
                          onChange={(e) => handleFields(VEHICLE_FIELDS.MODEL, e.target.value, "")}
                          marginBottom="10px"
                          paddingRight="5px"
                        >
                          <option disabled>Please select</option>
                          {modelList.map((record) => (
                            <option key={record} value={record}>
                              {record}
                            </option>
                          ))}
                        </CustomDropdown>
                      </CompactCol>
                    </SLRowLayout>
                  </SLGridLayout>
                </CompactCol>
              </SLRowLayout>
            </SLGridLayout>
          )}

          <SLGridLayout>
            <SLRowLayout css={{ marginTop: ".1rem", paddingLeft: "10px !important" }}>
              <CompactCol colSizes={{ mobile: 2, tablet: 6, desktop: 12 }}>
                <SLGridLayout>
                  <SLRowLayout css={{ marginTop: ".1rem", paddingLeft: "10px !important" }}>
                    <CompactCol colSizes={{ mobile: 4, tablet: 12, desktop: 2 }}>
                      <StatusWrapper>
                        <Icon
                          className="trash-icon"
                          aria-label="InternationalSymbolOfAccess"
                          alt="InternationalSymbolOfAccess"
                          name="international-symbol-of-access"
                          size="XLarge"
                          style={{ cursor: "pointer", outline: "none", width: "1.3rem", blockSize: "1.3rem" }}
                          color="#000000"
                        />
                        <CustomCheckbox
                          role="checkbox"
                          data-testid="wheelChair-checkbox"
                          aria-checked={wheelChair}
                          width="10px"
                          error={false}
                          errorText=""
                          value={wheelChair}
                          selected={wheelChair}
                          onChange={(e) => handleFields(VEHICLE_FIELDS.WHEELCHAIR, e.target.value, "")}
                        />
                      </StatusWrapper>
                    </CompactCol>
                    <CompactCol colSizes={{ mobile: 4, tablet: 12, desktop: 2 }}>
                      <StatusWrapper>
                        <Icon
                          className="smart-switch"
                          aria-label="smart-switch"
                          alt="smart-switch"
                          name="smart-switch"
                          size="Large"
                          color="#000000"
                          style={{ cursor: "pointer", outline: "none", width: "1.3rem", blockSize: "1.3rem" }}
                        />
                        <CustomCheckbox
                          role="checkbox"
                          data-testid="killSwitch-checkbox"
                          aria-checked={killSwitch}
                          width="10px"
                          error={false}
                          errorText=""
                          value={killSwitch}
                          selected={killSwitch}
                          onChange={(e) => handleFields(VEHICLE_FIELDS.KILLSWITCH, e.target.value, "")}
                        />
                      </StatusWrapper>
                    </CompactCol>
                    <CompactCol colSizes={{ mobile: 4, tablet: 12, desktop: 2 }}>
                      <StatusWrapper>
                        <Icon
                          className="police-fleet"
                          aria-label="police-fleet"
                          alt="police-fleet"
                          name="police-fleet"
                          size="Large"
                          color="#000000"
                          style={{ cursor: "pointer", outline: "none", width: "1.3rem", blockSize: "1.3rem" }}
                        />
                        <CustomCheckbox
                          role="checkbox"
                          data-testid="ambulance-checkbox"
                          aria-checked={ambulance}
                          width="10px"
                          error={false}
                          errorText=""
                          value={ambulance}
                          selected={ambulance}
                          onChange={(e) => handleFields(VEHICLE_FIELDS.AMBULANCE, e.target.value, "")}
                        />
                      </StatusWrapper>
                    </CompactCol>
                  </SLRowLayout>
                </SLGridLayout>
              </CompactCol>
            </SLRowLayout>
          </SLGridLayout>

          <FlexRow style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              aria-label="Yes"
              role="button"
              css={{ padding: "0 10px 0 10px" }}
              size="small"
              disabled={disableConfirmChange}
              data-testid="confirm-change-btn"
              onClick={() => {
                onConfirmChangeClick();
              }}
            >
              Confirm Changes
            </Button>
          </FlexRow>
        </ModalBody>
      </Modal>
    </>
  );
};
export default VehicleChangeModal;
